import FlowContainer from "@/components/pages/AppPages/Pages/components/FlowContainer";
import { getUserSignedInState } from "@brandclub/common-ui";

import { useTrackActions } from "@/utils/hooks/useTracking";
// import { getRetailerCheckoutOrderStatus } from "@/api/rest/checkoutApi";
// import { ShippingAddress } from "@/components/Checkout/types";
import { AnalyticsTrackingEvent } from "@brandclub/common-ui";
import React, { useEffect, useState } from "react";

// import GeoConfetti from "./GeoConfetti";
import { useCallback, useRef } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import GeoConfetti from "./GeoConfetti";

interface OrderConfirmationProps {
  orderNumber?: string;
  customerName?: string;
  shippingAddress?: string;
  estimatedDeliveryDate?: Date;
  savedAmount?: number;
  savedAmountReason?: string;
  productImageUrl?: string;
}

type UserLoginStatus = "notChecked" | "loggedIn" | "notLoggedIn";

export const OrderSuccess: React.FC<OrderConfirmationProps> = () => {
  const location = useLocation();
  const { address } = location.state || {
    address: undefined,
  };
  const [searchParams] = useSearchParams();
  const [trackAction] = useTrackActions();
  const orderId = searchParams.get("orderId");
  const [userLoginStatus, setUserLoginStatus] =
    useState<UserLoginStatus>("notChecked");
  const [addressString, setAddressString] = useState<string>("");
  const hasTriggeredTrackRef = useRef(false);

  // Navigation function to go to checkout summary
  const navigate = useNavigate();
  const handleTrackOrderClick = useCallback(() => {
    trackAction?.(
      AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUCCESS_TRACK_ORDER_CLICK,
      {
        orderId,
      }
    );
    navigate(`/checkoutSummary?orderId=${orderId}`);
  }, [orderId, navigate, trackAction]);

  // Build address string to geocode in GeoConfetti via Google JS API
  useEffect(() => {
    if (!address) return;
    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.state,
      address.zip || address.zipCode,
    ]
      .filter(Boolean)
      .join(", ");
    setAddressString(parts);
  }, [address]);

  // Fallback: fetch address by orderId if it wasn't provided via navigation state

  useEffect(() => {
    if (orderId) {
      trackAction?.(
        AnalyticsTrackingEvent.CHECKOUT_CONNECT_SUCCESS_SCREEN_VISIT,
        {
          orderId,
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  // Track CHECKOUT_ORDER_SUCCESS when purchaseConfirmed is set in localStorage
  useEffect(() => {
    if (orderId) {
      try {
        const purchaseConfirmedData = localStorage.getItem("purchaseConfirmed");
        if (purchaseConfirmedData) {
          const parsed = JSON.parse(purchaseConfirmedData);
          // Only if confirmedAt is within the last 5 minutes
          if (
            parsed.orderId === orderId &&
            parsed.confirmedAt &&
            Math.abs(Date.now() - new Date(parsed.confirmedAt).getTime()) < 5 * 60 * 1000
          ) {
            trackAction?.(AnalyticsTrackingEvent.CHECKOUT_ORDER_SUCCESS, {
              orderId: orderId,
              checkoutPhase: "post_submission",
              confirmedAt: parsed.confirmedAt,
              checkoutItems: parsed.checkoutItems,
            });
            // Clear the purchaseConfirmed data after processing
            localStorage.removeItem("purchaseConfirmed");
          }
        }
      } catch (error) {
        console.error("Error processing purchaseConfirmed data:", error);
      }
    }
  }, [orderId, trackAction]);

  useEffect(() => {
    const checkUserInfo = async () => {
      try {
        const { userInfo, signedIn } = await getUserSignedInState();
        if (userInfo && signedIn) {
          setUserLoginStatus("loggedIn");
          return;
        }
      } catch (error) {
        console.error(error);
      }
      // this route requires login, so redirect to login page
      setUserLoginStatus("notLoggedIn");
      navigate("/signin", {
        replace: true,
        state: {
          redirectPath: {
            pathname: "/checkout/thankyou",
            search: searchParams.toString(),
          },
        },
      });
    };

    checkUserInfo();
  }, [navigate, searchParams]);

  useEffect(() => {
    if (orderId === null && userLoginStatus === "loggedIn") {
      console.warn("no order id found in the URL; navigating to purchase");
      navigate("/dashboard/purchase");
    }
  }, [orderId, navigate, userLoginStatus]);

  // Auto track order:
  // - If address exists: wait 10s then navigate
  // - If address is missing: navigate immediately
  // Only trigger once and only after user is logged in
  useEffect(() => {
    if (userLoginStatus !== "loggedIn") return;
    if (orderId === null) return; // handled by the purchase redirect effect above
    if (hasTriggeredTrackRef.current) return;

    if (!address) {
      hasTriggeredTrackRef.current = true;
      handleTrackOrderClick();
      return;
    }

    const timerId = window.setTimeout(() => {
      if (hasTriggeredTrackRef.current) return;
      hasTriggeredTrackRef.current = true;
      handleTrackOrderClick();
    }, 10000);

    return () => {
      window.clearTimeout(timerId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, userLoginStatus, orderId, handleTrackOrderClick]);

  return (
    <FlowContainer sx={{ paddingBottom: 0 }}>
      <GeoConfetti addressString={addressString} />
    </FlowContainer>
  );
};

export default OrderSuccess;
